import { SceneContext, sceneState, type SceneState } from '@/lib/threeScene/SceneContext';
import { renderingState, type RenderingState } from '@/lib/threeScene/renderingState';
import { axesNode, type AxesNode } from '@/lib/threeScene/nodes/axes';
import { gridNode, type GridNode } from '@/lib/threeScene/nodes/grid';
import { type StandardNode, updateNode } from '@/lib/threeScene/updateNode';
import {
    BufferGeometry,
    DirectionalLight,
    HemisphereLight,
    LineBasicMaterial,
    Matrix4,
    Scene,
    SphereGeometry,
    Vector3,
} from 'three';
import { addObject } from '@/lib/threeScene/object3d';
import { cameraState } from '@/lib/threeScene/camera';
import { vector3 } from '@/domain/geometry/vector3';
import { stopAll, type StopHandle } from '@/util';
import { type MeshNode, meshNode } from '@/lib/threeScene/nodes/mesh';
import { markRaw, ref } from 'vue';
import { makeStandardMaterial } from '@/lib/threeScene/materials/standardMaterial';
import { planeNode, type PlaneNode } from '@/lib/threeScene/nodes/plane';
import { updatePlaneMeshIntersection } from '@/lib/threeScene/planeMeshIntersection';
import { groupNode, type GroupNode } from '@/lib/threeScene/nodes/group';
import { type LineSegmentsNode, lineSegmentsNode } from '@/lib/threeScene/nodes/lineSegments';
import { watchImmediate } from '@vueuse/core';

//--------------------------------------------------------------------------------------------------
// Define the reactive scene state and related functions:

export type PrototypeSceneNode = StandardNode;

export type PrototypeSceneState = SceneState<StandardNode> & {
    /**
     * Rendering state does not have to be part of the scene-state, but it is
     * convenient if there is a 1-1 relationship between a scene and a viewport
     */
    rendering: RenderingState;

    planeOffset: number;

    /**
     * The nodes do not have to be part of the scene-state, as they can be added to the
     * node-map, but adding them to the state makes them easy to access.
     */
    nodes: {
        origin: AxesNode;
        group: GroupNode;
        group2: GroupNode;
        grid: GridNode;
        lines: LineSegmentsNode;
        sphere: MeshNode;
        sphere2: MeshNode;
        plane: PlaneNode;
    };
};

/**
 * Define the initial state of the scene
 */
export function prototypeSceneState(): PrototypeSceneState {
    return {
        ...sceneState(),
        planeOffset: 0,
        rendering: renderingState({
            camera: cameraState({
                height: 1500,
                position: vector3(100, 300, 1000),
                up: vector3(0, 1, 0),
                target: vector3(),
            }),
        }),
        nodes: {
            origin: axesNode('origin'),
            group: groupNode('group', {
                transform: new Matrix4().makeRotationZ(45).setPosition(-50, 200, 40),
                showAxes: true,
            }),
            group2: groupNode('group2', {
                transform: new Matrix4().makeRotationY(45).setPosition(100, 200, -50),
                showAxes: true,
            }),
            grid: gridNode('reference-grid'),
            lines: lineSegmentsNode('lines', {
                geometry: markRaw(
                    new BufferGeometry().setFromPoints([
                        vector3(50, 50, 100),
                        vector3(100, 50, 100),
                        vector3(100, 100, 100),
                        vector3(50, 100, 100),
                    ]),
                ),
                material: markRaw(new LineBasicMaterial({ color: '#0df6f6' })),
            }),
            sphere: meshNode('sphere', {
                transform: new Matrix4().makeRotationY(45),
                geometry: markRaw(new SphereGeometry(300, 128, 64)),
                material: markRaw(
                    makeStandardMaterial({
                        roughness: 0.4,
                        metalness: 0.5,
                        color: '#8630da',
                        transparent: true,
                        opacity: 0.5,
                    }),
                ),
                renderOrder: 1,
                visible: true,
            }),
            sphere2: meshNode('sphere2', {
                parent: 'group2',
                transform: new Matrix4().makeRotationX(30).setPosition(100, 100, 100),
                geometry: markRaw(new SphereGeometry(200, 128, 64)),
                material: markRaw(
                    makeStandardMaterial({
                        roughness: 0.4,
                        metalness: 0.5,
                        color: '#00917a',
                        transparent: true,
                        opacity: 0.5,
                    }),
                ),
                visible: true,
                renderOrder: 2,
            }),
            plane: planeNode('plane', {
                width: 800,
                height: 800,
                transform: new Matrix4()
                    .lookAt(new Vector3(0, 0, 0), new Vector3(1, 1, 1), new Vector3(0, 1, 0))
                    .setPosition(100, 0, 200),
                material: markRaw(
                    makeStandardMaterial({
                        roughness: 0.4,
                        metalness: 0.5,
                        color: '#da7730',
                        transparent: true,
                        opacity: 0.5,
                    }),
                ),
                renderOrder: 0,
                visible: true,
            }),
        },
    };
}

//--------------------------------------------------------------------------------------------------
// Define the scene context and related functions:

export class PrototypeSceneContext extends SceneContext<PrototypeSceneNode, PrototypeSceneState> {}

export function prototypeSceneContext(state: PrototypeSceneState): PrototypeSceneContext {
    const scene = prototypeScene();
    return new PrototypeSceneContext(scene, state);
}

/**
 * Create and configure unchanging aspects of the three-js scene
 */
export function prototypeScene(): Scene {
    const scene = new Scene();
    scene.up.set(0, 0, 1);

    addObject(scene, () => {
        const light = new DirectionalLight('#ffffff', 1);
        light.position.set(0, 0, 1000);
        light.updateMatrix();
        return light;
    });

    addObject(scene, () => {
        const light = new HemisphereLight('#b4d8df', '#675f5f', 5);
        light.position.set(0, 0, 2000);
        light.updateMatrix();
        return light;
    });

    return scene;
}

//--------------------------------------------------------------------------------------------------
// Define the scene update function

export function updatePrototypeScene(context: PrototypeSceneContext): StopHandle {
    return stopAll(
        ...Object.values(context.state.nodes).map((node) => updateNode(context, node)),
        watchImmediate(
            () => context.state.planeOffset,
            (offset) => {
                _updatePlaneTransform(offset, context.state.nodes.plane.transform);
            },
        ),
        updatePlaneMeshIntersection(
            context,
            null,
            'plane',
            'sphere',
            ref(markRaw(new LineBasicMaterial({ color: '#c918fa' }))),
        ),
        updatePlaneMeshIntersection(
            context,
            null,
            'plane',
            'sphere2',
            ref(markRaw(new LineBasicMaterial({ color: '#30dda7' }))),
        ),
    );
}

const _PLANE_NORMAL = new Vector3(1, 1, 1).normalize();

function _updatePlaneTransform(offset: number, transform: Matrix4): void {
    transform
        .lookAt(new Vector3(0, 0, 0), _PLANE_NORMAL, new Vector3(0, 1, 0))
        .setPosition(new Vector3(100, 0, 200).add(_PLANE_NORMAL.clone().multiplyScalar(offset)));
}
